import React from "react";
import { Plus, Trash } from "@phosphor-icons/react";

/**
 * Tiered bid increment editor.
 * value: [{up_to: number|null, step: number}, ...]
 * The LAST tier's up_to must be null (means "and beyond").
 */
export default function BidTiersEditor({ value, onChange }) {
  const tiers = Array.isArray(value) && value.length > 0 ? value : [{ up_to: null, step: 10 }];

  const update = (idx, field, v) => {
    const next = tiers.map((t, i) => i === idx ? { ...t, [field]: v } : t);
    onChange(next);
  };

  const remove = (idx) => {
    if (tiers.length === 1) return;
    let next = tiers.filter((_, i) => i !== idx);
    // ensure last tier's up_to is null (open-ended)
    next = next.map((t, i) => i === next.length - 1 ? { ...t, up_to: null } : t);
    onChange(next);
  };

  const add = () => {
    // convert current last (open-ended) into a bounded one, then append a new open-ended tier.
    const last = tiers[tiers.length - 1];
    const prevBound = tiers.length >= 2 ? (tiers[tiers.length - 2].up_to ?? 0) : 0;
    const newBound = Math.max(prevBound + 100, 100);
    const nextTiers = tiers.slice(0, -1).concat([{ ...last, up_to: newBound }, { up_to: null, step: (last.step || 10) * 2 }]);
    onChange(nextTiers);
  };

  return (
    <div className="space-y-2" data-testid="bid-tiers-editor">
      <div className="label-mono">Bid Increment Tiers</div>
      <div className="text-xs text-gray-500 mb-2">
        E.g. up to 100 pts → +10 · up to 500 pts → +20 · beyond → +50
      </div>
      <div className="space-y-2">
        {tiers.map((t, i) => {
          const prevUpTo = i === 0 ? 0 : (tiers[i - 1].up_to ?? 0);
          const isLast = i === tiers.length - 1;
          return (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`bid-tier-row-${i}`}>
              <div className="col-span-5 flex items-center gap-2 text-xs">
                <span className="label-mono">From</span>
                <span className="font-mono text-gray-600 bg-white rounded px-2 py-1 flex-1 text-center">{prevUpTo}</span>
              </div>
              <div className="col-span-4 flex items-center gap-2 text-xs">
                <span className="label-mono">Up&nbsp;to</span>
                {isLast ? (
                  <span className="font-mono text-gray-500 bg-white rounded px-2 py-1 flex-1 text-center">∞</span>
                ) : (
                  <input
                    type="number"
                    min={prevUpTo + 1}
                    value={t.up_to ?? ""}
                    onChange={(e) => update(i, "up_to", Number(e.target.value) || null)}
                    className="bg-white border border-gray-200 rounded px-2 py-1 text-center flex-1 focus:border-blue-500 outline-none"
                    data-testid={`bid-tier-upto-${i}`}
                  />
                )}
              </div>
              <div className="col-span-2 flex items-center gap-1 text-xs">
                <span className="label-mono">+</span>
                <input
                  type="number"
                  min="1"
                  value={t.step}
                  onChange={(e) => update(i, "step", Number(e.target.value) || 1)}
                  className="bg-white border border-gray-200 rounded px-2 py-1 text-center w-full focus:border-blue-500 outline-none"
                  data-testid={`bid-tier-step-${i}`}
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => remove(i)}
                  disabled={tiers.length === 1}
                  className="p-1 rounded hover:bg-red-950 disabled:opacity-30 disabled:hover:bg-transparent"
                  data-testid={`bid-tier-remove-${i}`}
                >
                  <Trash size={14} className="text-red-600" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={add}
        className="btn-ghost text-xs !py-1 !px-2 flex items-center gap-1 mt-2"
        data-testid="bid-tier-add"
      >
        <Plus size={12} /> Add Tier
      </button>
    </div>
  );
}
